import { TranslateService } from "@ngx-translate/core";
import { BaseColumn, BooleanColumn, ColumnType, DateColumn, FormDocumentColumn, IImportResponse, NumberColumn, StringColumn } from "./dataset-dinamico-columnas";
import * as ExcelJS from 'exceljs';
import { IColumnDinamico } from "../../../shared/interfaces/database/tramites_automaticos";

export class DatasetDinamico {
  static readonly POSTGRES_COLUMN_NAME_REGEX = /^[a-z0-9_]+$/;

  private _columns: BaseColumn<any>[];
  private _newColumns: BaseColumn<any>[] = []; // to track newly added columns
  private _data: any[];
  private _translations!: any;
  private _t: TranslateService

  constructor(data: any) {
    this._columns = []
    this._data = [];
    this._translations = data.translations || {};
    this._t = data.translateService;
  }

  get columns(): BaseColumn<any>[] {
    if(this._newColumns.length > 0){
      return [...this._columns, ...this._newColumns];
    }
    return this._columns;
  }

  get data(): any[] {
    return this._data;
  }

  set data(value: any[]) {
    this._data = value;
  }

  get translations(): any {
    return this._translations;
  }

  get newColumns(): BaseColumn<any>[] {
    return this._newColumns;
  }

  reset(){
    this._columns = [];
    this._newColumns = [];
    this._data = [];
  }

  loadColumns( toLoadColumns: IColumnDinamico[]){
    this.reset();
    toLoadColumns.forEach(col => {
      const column = this.initializeColumnByType(col.type, col);
      this._columns.push(column);
    });
  }

  //#region CSV import
  private parseCsvHeaderRow(row: string[], dataRow: string[]) {
    const uniqueHeaderValues = new Set(row);
    if (row.length !== uniqueHeaderValues.size) {
      throw new Error(this.translations.encabezadosUnicos);
    }

    for (let i = 0; i < row.length; i++) {
      const value = row[i];
      const columnsNumber = i + 1;
      if (!value) {
        throw new Error("El encabezado en la columna " + columnsNumber + " no tiene valor");
      }
      if (!DatasetDinamico.POSTGRES_COLUMN_NAME_REGEX.test(value)) {
        throw new Error("El encabezado en la columna " + columnsNumber + " no es un nombre de columna válido");
      }
      if (value.length > 63) {
        throw new Error("El encabezado en la columna " + columnsNumber + " excede el límite de 63 caracteres");
      }

      let type = ColumnType.String;
      let format = '';

      if (dataRow.length > i) {
        const dataValue = dataRow[i];
        if (typeof dataValue === 'string') {
          // Date detection
          if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dataValue)) {
            type = ColumnType.Date;
            format = 'm/d/yyyy';
          }
          // Currency detection with format
          else if (dataValue.startsWith('$')) {
            type = ColumnType.Number;
            // Count decimal places
            const decimalRegex = /\.(\d+)/;
            const decimalMatch = decimalRegex.exec(dataValue);
            const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;
            format = `"$"#,##0${decimalPlaces > 0 ? '.' + '0'.repeat(decimalPlaces) : ''}`;
          }
          // Percentage detection with format
          else if (dataValue.endsWith('%')) {
            type = ColumnType.Number;
            // Count decimal places
            const decimalRegex = /\.(\d+)/;
            const decimalMatch = decimalRegex.exec(dataValue);
            const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;
            format = `0${decimalPlaces > 0 ? '.' + '0'.repeat(decimalPlaces) : ''}%`;
          }
          // Regular number detection with format
          else if (!isNaN(Number(dataValue)) && dataValue !== '') {
            type = ColumnType.Number;
            // Count decimal places
            const decimalRegex = /\.(\d+)/;
            const decimalMatch = decimalRegex.exec(dataValue);
            const decimalPlaces = decimalMatch ? decimalMatch[1].length : 0;
            format = `#,##0${decimalPlaces > 0 ? '.' + '0'.repeat(decimalPlaces) : ''}`;
          }
          // Boolean detection
          else if (dataValue === 'true' || dataValue === 'false') {
            type = ColumnType.Boolean;
          }
        }
      }

      this.columns[i] = this.initializeColumnByType(type, {
        name: value.toLowerCase(),
        displayName: value,
        format: format
      });
    }
  }

  private parseCsvRow(row: string[]): any {
    let rowData: any = {};
    for (let i = 0; i < row.length; i++) {
      const column = this.columns[i];
      if (column === undefined) {
        throw new Error("El encabezado no tiene valor");
      }
      const value = row[i];
      // Same validation as Excel parser
      if (value === '') {
        rowData[column.name] = null;
      }
      else if (column.type === ExcelJS.ValueType.String) {
        // Validación que bloquea solo caracteres potencialmente peligrosos
        const dangerousCharsRegex = /[<>{}\[\]\\^`|]/g;

        if (dangerousCharsRegex.test(value)) {
          throw new Error("El valor \"" + value + "\" de la columna " + column.name + " contiene caracteres no permitidos por razones de seguridad.");
        }
        rowData[column.name] = value; // Keep as string
      }
      // Convert to number
      else if (column.type === ExcelJS.ValueType.Number) {
        // Handle currency and percentage by removing special characters
        let numValue = value;
        const isPercentage = typeof value === 'string' && value.includes('%');

        if (typeof value === 'string') {
          numValue = value.replace(/[$%,]/g, '');
        }

        // Convert to number and divide by 100 if it's a percentage
        const numberValue = Number(numValue);
        rowData[column.name] = isPercentage ? numberValue / 100 : numberValue;
      }
      // Convert to boolean
      else if (column.type === ExcelJS.ValueType.Boolean) {
        rowData[column.name] = value === 'true';
      }
      // Convert to date
      else if (column.type === ExcelJS.ValueType.Date) {
        rowData[column.name] = value;
      }
      // Default case
      else {
        rowData[column.name] = value;
      }
    }
    return rowData;
  }

  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let inQuotes = false;
    let currentValue = '';

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          // Double quotes inside quotes - add a single quote
          currentValue += '"';
          i++;
        } else {
          // Toggle quotes mode
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of value
        result.push(currentValue);
        currentValue = '';
      } else {
        // Add character to current value
        currentValue += char;
      }
    }

    // Add the last value
    result.push(currentValue);
    return result;
  }

  public parseCsv(text: string): Promise<IImportResponse> {
    return new Promise((resolve) => {
      try {
        const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);

        if (lines.length < 1) {
          resolve({ valid: false, msg: "No se encontraron datos" } as IImportResponse);
          return;
        }

        let tableData: any[] = [];
        this._columns = [];
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const row = this.parseCsvLine(line);
          if (i === 0) {
            const dataRow = lines.length > 1 ? this.parseCsvLine(lines[1]) : [];
            this.parseCsvHeaderRow(row, dataRow);
          }
          else {
            const rowData = this.parseCsvRow(row);
            tableData.push(rowData);
          }
        }

        this.data = tableData;
        resolve({ valid: true, msg: "Éxito" } as IImportResponse);
      } catch (error) {
        if (error instanceof Error) {
          resolve({ valid: false, msg: error.message } as IImportResponse);
        } else {
          resolve({ valid: false, msg: 'Error al parsear CSV' } as IImportResponse);
        }
      }
    });
  }
  //#endregion CSV import

  //#region Excel import

  private parseExcelHeaderRow(row: ExcelJS.Row, dataRow: ExcelJS.Row) {
    const headerValues = Object.values(row.values);

    const uniqueHeaderValues = new Set(headerValues);
    if (headerValues.length !== uniqueHeaderValues.size)
      throw new Error(this.translations.encabezadosUnicos);

    row.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell, colNumber: number) => {
      const value = cell.value as string;
      //validaciones
      if (!value) {
        throw new Error(this._t.instant('msg.headerCellNoValue', { cell: cell.address }));
      }
      if (!DatasetDinamico.POSTGRES_COLUMN_NAME_REGEX.test(value)) {
        throw new Error(this._t.instant('msg.headerInvalidName', { value: value, cell: cell.address }));
      }
      if (value.length > 63) {
        throw new Error(this._t.instant('msg.headerTooLong', { value, cell: cell.address }));
      }

      let type = ColumnType.String;
      switch (dataRow.getCell(colNumber).type) {
        case ExcelJS.ValueType.Null:
          type = ColumnType.String;
          break;
        case ExcelJS.ValueType.Number:
          type = ColumnType.Number;
          break;
        case ExcelJS.ValueType.String:
          type = ColumnType.String;
          break;
        case ExcelJS.ValueType.Date:
          type = ColumnType.Date;
          break;
        case ExcelJS.ValueType.Hyperlink:
          type = ColumnType.String;
          break;
        case ExcelJS.ValueType.Formula:
          type = ColumnType.String;
          break;
        case ExcelJS.ValueType.SharedString:
          type = ColumnType.String;
          break;
        case ExcelJS.ValueType.RichText:
          type = ColumnType.String;
          break;
        case ExcelJS.ValueType.Boolean:
          type = ColumnType.Boolean;
          break;
        case ExcelJS.ValueType.Error:
          type = ColumnType.String;
          break;
      }

      this.columns[colNumber - 1] = this.initializeColumnByType(type, {
        name: value.toLowerCase(),
        displayName: value,
        format: cell.numFmt
      });
    });
  }

  private parseExcelRow(row: ExcelJS.Row): any {
    const rowData: any = {};
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const column = this.columns[colNumber - 1];
      if (column === undefined)
        throw new Error(this._t.instant('msg.headerCellNoValue', { cell: cell.address }));

      const value = cell.value;
      if (typeof value === 'string') {
        // Validación que bloquea solo caracteres potencialmente peligrosos
        const dangerousCharsRegex = /[<>{}\[\]\\^`|]/g;
        if (dangerousCharsRegex.test(value)) {
          throw new Error(this._t.instant('msg.cellInvalidCharacters', { cell: cell.address }));
        }
      }
      rowData[column.name] = value;
    });
    return rowData;
  }

  public parseExcel(arrayBuffer: any): Promise<IImportResponse> {
    const workbook = new ExcelJS.Workbook();
    return workbook.xlsx.load(arrayBuffer).then((workbook) => {
      let tableData: any[] = [];
      this._columns = [];
      const firstWorksheet = workbook.worksheets[0];
      try {
        firstWorksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber === 1) {
            const dataRow = firstWorksheet.getRow(2);//esto es para obtener el tipo de dato
            this.parseExcelHeaderRow(row, dataRow);
          }
          else {
            const rowData = this.parseExcelRow(row);
            tableData.push(rowData);
          }
        });
      } catch (error) {
        if (error instanceof Error) {
          return { valid: false, msg: error.message } as IImportResponse;
        }
      }
      this._data = tableData;
      return { valid: true, msg: "success" } as IImportResponse;
    });
  }
  //#endregion Excel import

  initializeColumnByType(type: ColumnType, data: any) {
    let columns: any;
    switch (type) {
      case ColumnType.String:
        columns = new StringColumn(data);
        break;
      case ColumnType.Number:
        columns = new NumberColumn(data);
        break;
      case ColumnType.Date:
        columns = new DateColumn(data);
        break;
      case ColumnType.Boolean:
        columns = new BooleanColumn(data);
        break;
      case ColumnType.FormDocument:
        columns = new FormDocumentColumn(data);
        break;
      default:
        columns = new StringColumn(data);
        break;
    }
    return columns;
  }

  addNewColumn(type: ColumnType, data: any) {
    const newColumn = this.initializeColumnByType(type, data);
    this._newColumns.push(newColumn);
  }

  toJSON(){
    return this._columns.map(col => col.toJSON());
  }
}